import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { useMemo } from 'react';

import { Params } from './types';
import { prepareBucketedRegimeData } from '../../common/utils/signal-utils';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  fullScreen: boolean;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  params: Params;
  context?: ts.types.signal.SignalAnalysisContext;
  title: string;
};

const Chart = ({ id, readOnly, fullScreen, loadWidgetData, file, params, context, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.HeatMapData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedTab, setSelectedTab] = React.useState<'performance' | 'count' | 'volatility' | 'information_ratio'>(
    'performance'
  );

  const resources = useSelector((state) => state.resources);

  const timeSeries = useSelector((state) => state.resources.time_series);
  const signals = useSelector((state) => state.resources.signals);

  const pts = React.useMemo(() => {
    return timeSeries.find((ts) => ts.id == params.time_series_id);
  }, [params.time_series_id]);

  const currSig = React.useMemo(() => {
    return signals.find((s) => s.id == context.signal_id);
  }, [context.signal_id]);

  const loadData = async (tab: 'performance' | 'count' | 'volatility' | 'information_ratio') => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          query: ['tab', 'equals', tab],
        },
      });

      if (response) {
        setData(response.data as ts.types.widgets.HeatMapData);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData(selectedTab);
    return () => setData(null);
  }, [file, selectedTab]);

  const formattedData = useMemo(() => {
    if (_.isNil(data)) return [];
    return prepareBucketedRegimeData(data, params.discrete, params.quantiles, params.buckets, resources);
  }, [data]);

  const getTabName = (t = selectedTab) => {
    if (t == 'count') return 'Avg Count';
    return _.startCase(t);
  };

  const getTabs = () => {
    return (
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(e, newTab) => setSelectedTab(newTab)}
        aria-label="Tabs"
      >
        {['performance', 'count', 'volatility', 'information_ratio'].map((tab) => (
          <mui.core.Tab key={tab} label={getTabName(tab as any)} value={tab} />
        ))}
      </mui.core.Tabs>
    );
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        <ui.HeatmapChart
          id={id}
          numberFormat={selectedTab === 'performance' || selectedTab === 'volatility' ? '#.##%' : '#.##'}
          data={formattedData}
          xAxisField="bucket1"
          yAxisField="bucket2"
          xAxisLabel={pts.name}
          yAxisLabel={currSig.name}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
          fullScreen={fullScreen}
          readOnly={readOnly}
          labelValues
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
